import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { Location } from "../../models/location.model";
import { citiesService } from "../../service/cities.service";
import { countryService } from "../../service/country.service";
import { locationService } from "../../service/location.service";
import { statesService } from "../../service/state.service";
import { zoneService } from "../../service/zone.service";
import PageHeader from "../PageHeader";

type DropdownOption = { label: string; value: number };

const LocationComponent: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [location, setLocation] = useState<Partial<Location>>({});
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [selectedState, setSelectedState] = useState<number | null>(null);
  const [selectedCity, setSelectedCity] = useState<number | null>(null);
  const [countries, setCountries] = useState<DropdownOption[]>([]);
  const [states, setStates] = useState<DropdownOption[]>([]);
  const [cities, setCities] = useState<DropdownOption[]>([]);
  const [zones, setZones] = useState<DropdownOption[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Initialize pagination state from localStorage
  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem("locationListPage");
    return savedPage ? parseInt(savedPage) : 1;
  });
  const [rows, setRows] = useState(() => {
    const savedRows = localStorage.getItem("locationListRows");
    return savedRows ? parseInt(savedRows) : 10;
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sortField, setSortField] = useState<string>(() => {
    return localStorage.getItem("locationListSortField") || "name";
  });
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1>(() => {
    const savedOrder = localStorage.getItem("locationListSortOrder");
    return savedOrder ? (parseInt(savedOrder) as 1 | 0 | -1) : 1;
  });

  const loadLocations = async () => {
    setListLoading(true);
    try {
      // Create filters object with selected values
      const filters = {
        pageNumber: page,
        pageSize: rows,
        sortField,
        sortOrder: sortOrder === -1 ? "desc" : "asc",
        globalFilter,
        // Add filter parameters
        ...(selectedCountry && { countryId: selectedCountry }),
        ...(selectedState && { stateId: selectedState }),
        ...(selectedCity && { cityId: selectedCity }),
      };

      const result = await locationService.getAllPaginated(filters);
      const mappedLocations = (result?.items ?? []).map((item: any) => ({
        ...item,
        id: typeof item.id === "string" ? parseInt(item.id, 10) : item.id,
      }));

      // Only set locations that match all selected filters
      const filteredLocations = selectedCity
        ? mappedLocations.filter((loc) => loc.cityId === selectedCity)
        : mappedLocations;

      setLocations(filteredLocations);
      setAllLocations(filteredLocations);
      setTotalRecords(result?.totalCount ?? 0);
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
      setAllLocations([]);
      setTotalRecords(0);
    } finally {
      setListLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadLocations();
  }, [page, rows, sortField, sortOrder, globalFilter]);

  // Load filter options
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countriesResult = await countryService.getAll();
        setCountries(
          countriesResult.map((c: any) => ({ label: c.name, value: c.id }))
        );
      } catch {
        setCountries([]);
      }
    };
    fetchCountries();
  }, [success]);

  // Apply filters - only load when all filters are selected
  useEffect(() => {
    // Only reset pagination if filters actually changed
    const prevFilters = JSON.parse(
      localStorage.getItem("locationFilters") || "{}"
    );
    const currentFilters = {
      countryId: selectedCountry,
      stateId: selectedState,
      cityId: selectedCity,
    };

    if (JSON.stringify(prevFilters) !== JSON.stringify(currentFilters)) {
      setPage(1);
      localStorage.setItem("locationListPage", "1");
      localStorage.setItem("locationFilters", JSON.stringify(currentFilters));
    }

    // Load locations only when all filters are selected
    if (selectedCountry && selectedState && selectedCity) {
      loadLocations();
    } else {
      // Clear locations when not all filters are selected
      setLocations([]);
      setTotalRecords(0);
    }
  }, [selectedCountry, selectedState, selectedCity]);

  useEffect(() => {
    citiesService.getAll().then((res: any) => {
      const cityOptions = res.map((city: any) => ({
        label: city.name,
        value: city.id,
      }));
      setCities(cityOptions);
    });
  }, []);

  useEffect(() => {
    const fetchZones = async () => {
      try {
        const zonesResult = await zoneService.getAll();
        setZones(zonesResult.map((z: any) => ({ label: z.name, value: z.id })));
      } catch {
        setZones([]);
      }
    };

    fetchZones();
  }, []);

  const handleCountryChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const countryId = Number(e.target.value);
    // Reset all dependent selections
    setSelectedCountry(countryId || null);
    setSelectedState(null);
    setSelectedCity(null);
    setCities([]);
    setStates([]);
    setLocation((prev) => ({ ...prev, cityId: 0 }));

    if (countryId) {
      try {
        const result = await statesService.getByCountryId(countryId);
        setStates(result.map((s: any) => ({ label: s.name, value: s.id })));
      } catch {
        setStates([]);
      }
    }
  };

  const handleStateChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stateId = Number(e.target.value);
    // Reset city selection
    setSelectedState(stateId || null);
    setSelectedCity(null);
    setCities([]);
    setLocation((prev) => ({ ...prev, cityId: 0 }));

    if (stateId) {
      try {
        const result = await citiesService.getByStateId(stateId);
        const mapped = result.map((c: any) => ({ label: c.name, value: c.id }));
        setCities(mapped);
      } catch {
        setCities([]);
      }
    }
  };
  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cityId = Number(e.target.value);
    setSelectedCity(cityId);
    setLocation((prev) => ({ ...prev, cityId }));
  };
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    const parsedValue =
      name === "cityId" || name === "zoneId" ? Number(value) : value;
    setLocation({ ...location, [name]: parsedValue });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (location.id) {
        const updated = await locationService.update(
          location.id,
          location as Location
        );
        setLocations((prev) =>
          prev.map((l) => (l.id === location.id ? updated : l))
        );
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Location updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        const created = await locationService.create(location as Location);
        setLocations((prev) => [...prev, created]);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Location created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
      setSuccess(true);
      setShowForm(false);
      setLocation({});
      // Reload the current page without resetting pagination
      loadLocations();
    } catch (err: any) {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to save location.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rowData: Location) => {
    setLocation(rowData);
    // Set the selected city, state, and country for proper form population
    if (rowData.cityId) {
      setSelectedCity(rowData.cityId);
      const selectedCityData = cities.find((c) => c.value === rowData.cityId);
      if (selectedCityData) {
        // Find the state from the city (this might need backend support)
        // For now, we'll just set the selected values
        setSelectedCountry(null); // We'd need to determine this from the city
        setSelectedState(null); // We'd need to determine this from the city
      }
    }
    setShowForm(true);
  };

  const handleStatus = async (rowData: Location) => {
    if (!rowData.id) return;
    try {
      await locationService.updateStatus(rowData.id);
      setSuccess(true);
      // Reload the current page without resetting pagination
      loadLocations();
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Location status updated successfully.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch {
      Swal.fire("Error", "Failed to delete location.", "error");
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to update location status.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  };

  const actionBodyTemplate = (rowData: Location) => (
    <div className="flex gap-2">
      <Button
        icon={<Icon.PencilSquare size={20} />}
        onClick={() => handleEdit(rowData)}
        tooltip="Edit"
        severity="success"
        text
        rounded
      />
      <Button
        icon={
          rowData.isActive ? <Icon.Unlock size={20} /> : <Icon.Lock size={20} />
        }
        onClick={() => handleStatus(rowData)}
        tooltip="Status"
        severity={rowData.isActive ? "success" : "danger"}
        text
        rounded
      />
    </div>
  );

  return (
    <div className="content has-nobreadcrumbs">
      <PageHeader
        title="Location List"
        showBackButton={false}
        beforeTitle={
          <Button
            icon="pi pi-refresh"
            rounded
            text
            severity="secondary"
            onClick={loadLocations}
            tooltip="Refresh"
            tooltipOptions={{ position: "bottom" }}
            aria-label="Refresh Locations"
          />
        }
      />
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <div className="d-flex align-items-center gap-2 my-3 flex-wrap">
                <IconField iconPosition="left" className="flex-grow-1">
                  <InputIcon>
                    <Icon.Search className="tx-12" />
                  </InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                      setPage(1);
                      localStorage.setItem("locationListPage", "1");
                    }}
                    placeholder="Search"
                    style={{ minWidth: 250 }}
                  />
                </IconField>

                <Dropdown
                  value={selectedCountry}
                  onChange={(e) =>
                    handleCountryChange({
                      target: { value: e.value },
                    } as React.ChangeEvent<HTMLSelectElement>)
                  }
                  options={countries}
                  placeholder="Country"
                  className="w-12rem"
                  appendTo="self"
                />

                <Dropdown
                  value={selectedState}
                  onChange={(e) =>
                    handleStateChange({
                      target: { value: e.value },
                    } as React.ChangeEvent<HTMLSelectElement>)
                  }
                  options={states}
                  placeholder="State"
                  className="w-12rem"
                  disabled={!selectedCountry}
                  appendTo="self"
                />

                <Dropdown
                  value={selectedCity}
                  onChange={(e) =>
                    handleCityChange({
                      target: { value: e.value },
                    } as React.ChangeEvent<HTMLSelectElement>)
                  }
                  options={cities}
                  placeholder="City"
                  className="w-12rem"
                  disabled={!selectedState}
                  appendTo="self"
                />

                <Button
                  label="Create Location"
                  icon={<Icon.PlusCircle />}
                  onClick={() => {
                    setLocation({
                      cityId: selectedCity ?? 0,
                      zoneId: undefined,
                    });
                    setShowForm(true);
                  }}
                  severity="info"
                  className="min-w-min"
                />
              </div>

              <DataTable
                value={locations}
                dataKey="id"
                paginator
                first={(page - 1) * rows}
                rows={rows}
                totalRecords={totalRecords}
                onPage={(e) => {
                  const newPage = Math.floor(e.first / e.rows) + 1;
                  setPage(newPage);
                  setRows(e.rows);
                  // Persist pagination state
                  localStorage.setItem("locationListPage", newPage.toString());
                  localStorage.setItem("locationListRows", e.rows.toString());
                }}
                onSort={(e) => {
                  const newSortField = e.sortField || "name";
                  const newSortOrder = e.sortOrder as 1 | -1 | 0;
                  setSortField(newSortField);
                  setSortOrder(newSortOrder);
                  localStorage.setItem("locationListSortField", newSortField);
                  localStorage.setItem(
                    "locationListSortOrder",
                    newSortOrder.toString()
                  );
                }}
                sortField={sortField}
                sortOrder={sortOrder}
                lazy
                loading={listLoading}
                rowsPerPageOptions={[5, 10, 20]}
                stripedRows
                sortMode="single"
                header={() => (
                  <div className="selected-filters p-2 bg-light border rounded mb-2">
                    <strong>Selected Filters: </strong>
                    {selectedCountry ? (
                      <span className="badge bg-primary me-2">
                        Country:{" "}
                        {
                          countries.find((c) => c.value === selectedCountry)
                            ?.label
                        }
                      </span>
                    ) : null}
                    {selectedState ? (
                      <span className="badge bg-info me-2">
                        State:{" "}
                        {states.find((s) => s.value === selectedState)?.label}
                      </span>
                    ) : null}
                    {selectedCity ? (
                      <span className="badge bg-success me-2">
                        City:{" "}
                        {cities.find((c) => c.value === selectedCity)?.label}
                      </span>
                    ) : null}
                    {!selectedCountry && !selectedState && !selectedCity && (
                      <span className="text-muted">No filters selected</span>
                    )}
                  </div>
                )}
                emptyMessage={
                  !selectedCountry && !selectedState && !selectedCity
                    ? "Please select a country, state, or city to view locations."
                    : "No locations found for the selected filters."
                }
                className="grid-table"
              >
                <Column field="name" header="Name" sortable />
                <Column field="locationType" header="Type" sortable />
                <Column
                  field="adjacentLocations"
                  header="Adjacent Locations"
                  sortable
                />
                <Column
                  field="cityId"
                  header="City"
                  sortable
                  body={(row) =>
                    cities.find((c) => c.value === row.cityId)?.label
                  }
                />
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ minWidth: "120px" }}
                />
              </DataTable>

              {showForm && (
                <div
                  className="modal show d-block"
                  tabIndex={-1}
                  style={{ background: "rgba(0,0,0,0.3)" }}
                >
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {location.id ? "Update Location" : "Create Location"}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowForm(false)}
                        ></button>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                          {/* 1st row: Location, Country */}
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Location:</label>
                              <input
                                className="form-control"
                                name="name"
                                value={location.name || ""}
                                onChange={handleChange}
                                required
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Country:</label>
                              <select
                                className="form-select"
                                value={selectedCountry ?? ""}
                                onChange={handleCountryChange}
                                required
                              >
                                <option value="">Select Country</option>
                                {countries.map((c) => (
                                  <option key={c.value} value={c.value}>
                                    {c.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          {/* 2nd row: State, City */}
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">State:</label>
                              <select
                                className="form-select"
                                value={selectedState ?? ""}
                                onChange={handleStateChange}
                                required
                                disabled={!selectedCountry}
                              >
                                <option value="">Select State</option>
                                {states.map((s) => (
                                  <option key={s.value} value={s.value}>
                                    {s.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">City:</label>
                              <select
                                className="form-select"
                                name="cityId"
                                value={location.cityId || ""}
                                onChange={handleCityChange}
                                required
                                disabled={!selectedState}
                              >
                                <option value="">Select City</option>
                                {cities.map((city) => (
                                  <option key={city.value} value={city.value}>
                                    {city.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          {/* 3rd row: Location Type, Adjacent Locations */}
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                Location Type:
                              </label>
                              <select
                                className="form-select"
                                name="locationType"
                                value={location.locationType || ""}
                                onChange={handleChange}
                                required
                              >
                                <option value="">Select Type</option>
                                <option value="Normal">Normal</option>
                                <option value="Adjacent">Adjacent</option>
                              </select>
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                Adjacent Locations:
                              </label>
                              <input
                                className="form-control"
                                name="adjacentLocations"
                                value={location.adjacentLocations || ""}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          {/* 4th row: Zone */}
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">Zone:</label>
                              <select
                                className="form-select"
                                name="zoneId"
                                value={location.zoneId || ""}
                                onChange={handleChange}
                              >
                                <option value="">Select Zone</option>
                                {zones.map((zone) => (
                                  <option key={zone.value} value={zone.value}>
                                    {zone.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          {error && (
                            <div className="alert alert-danger">{error}</div>
                          )}
                        </div>
                        <div className="modal-footer">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading
                              ? location.id
                                ? "Updating..."
                                : "Creating..."
                              : location.id
                              ? "Update"
                              : "Create"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowForm(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationComponent;
export { LocationComponent };
